import React, { useState, useEffect } from 'react';
import { X, Search, FileText } from 'lucide-react';
import ContractService from '../../services/contractService';

/**
 * ContractSelectorModal
 * Replaces RfpSelectorModal for the sourcing E-Signature flow.
 * Loads contracts from the same endpoint used by /contracts/management.
 */
const ContractSelectorModal = ({ onSelect, onClose }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const response = await ContractService.getContracts({ limit: 200, includeInactive: false });
      // getContracts returns { data: [...], pagination: {...} } or the array directly
      const list =
        response?.data ||
        (Array.isArray(response) ? response : []);
      setContracts(list);
    } catch (err) {
      console.error('Failed to load contracts for e-signature selector', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = contracts.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.id || c.contractId || '').toLowerCase().includes(q) ||
      (c.title || '').toLowerCase().includes(q) ||
      (c.counterparty || '').toLowerCase().includes(q)
    );
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '0',
          width: '540px',
          maxWidth: '92vw',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)' }}>
            Select Contract for E-Signature
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: 'var(--text-secondary)',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-secondary)',
              }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by Contract ID or Contract Title..."
              style={{
                width: '100%',
                padding: '10px 12px 10px 36px',
                border: '1px solid var(--border-color)',
                borderRadius: '0',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* Contract list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              Loading contracts...
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              {search ? 'No contracts match your search' : 'No contracts available'}
            </div>
          ) : (
            filtered.map((contract) => {
              const contractId = contract.id || contract.contractId || '';
              const title = contract.title || contract.documentName || 'Untitled Contract';
              const status = contract.status || '';
              const counterparty = contract.counterparty || '';

              return (
                <div
                  key={contract._id || contractId}
                  onClick={() => onSelect(contract)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 24px',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    borderBottom: '1px solid #f3f4f6',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <FileText size={18} style={{ color: 'var(--gold-primary, #b8860b)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: 'var(--text-secondary)',
                        marginBottom: '2px',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      {contractId}
                    </div>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'var(--text-primary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      {title}
                    </div>
                    {counterparty && (
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        {counterparty}
                      </div>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      background: '#f3f4f6',
                      borderRadius: '4px',
                      color: 'var(--text-secondary)',
                      fontWeight: 500,
                      flexShrink: 0,
                      textTransform: 'uppercase',
                    }}
                  >
                    {status || 'DRAFT'}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractSelectorModal;
